import jsPDF from "jspdf";
import { pdfSafe } from "./pdfText";

export interface PhaseLabSnapshot {
  substanceName: string;
  formula: string;
  meltC: number;
  boilC: number;
  Lf: number;
  Lv: number;
  cs: number;
  cl: number;
  cg: number;
  progressPct: number;
  qPerKg: number;
  totalQ: number;
  tempC: number;
  phase: string;
  kePct: number;
  pePct: number;
}

export function downloadPhaseLabPdf(s: PhaseLabSnapshot) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 56;
  let y = 60;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("ELEMENTPAD · PHASE-CHANGE LAB", M, 32);
  doc.setTextColor(37, 99, 235);
  doc.text(new Date().toLocaleDateString().toUpperCase(), PAGE_W - M, 32, { align: "right" });
  doc.setDrawColor(220);
  doc.line(M, 40, PAGE_W - M, 40);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(20);
  doc.text(pdfSafe(`Phase-Change Snapshot — ${s.substanceName}`), M, y);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(80);
  doc.text(pdfSafe(`Formula: ${s.formula}`), M, y);
  y += 24;

  // Name / date row for printable use
  doc.setFontSize(10);
  doc.setTextColor(60);
  ["Name:", "Date:", "Class:"].forEach((label, i) => {
    const colW = (PAGE_W - 2 * M) / 3;
    const x = M + colW * i;
    doc.text(label, x, y);
    doc.setDrawColor(180);
    doc.line(x + 40, y + 2, x + colW - 12, y + 2);
  });
  y += 26;

  function section(title: string) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(20);
    doc.text(title, M, y);
    y += 14;
    doc.setDrawColor(230);
    doc.line(M, y - 4, PAGE_W - M, y - 4);
  }

  function row(k: string, v: string) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(pdfSafe(k), M, y);
    doc.setTextColor(20);
    doc.text(pdfSafe(v), M + 240, y);
    y += 16;
  }

  section("Current state");
  row("Phase", s.phase);
  row("Temperature", `${s.tempC.toFixed(1)} °C`);
  row("Heat added (Q)", `${s.qPerKg.toFixed(1)} kJ/kg  (${s.progressPct}% of ${s.totalQ.toFixed(0)} kJ/kg)`);
  row("KE (∝ T)", `${s.kePct}%`);
  row("PE (intermolecular)", `${s.pePct}%`);
  y += 8;

  section("Substance constants");
  row("Melting point", `${s.meltC} °C`);
  row("Boiling point", `${s.boilC} °C`);
  row("ΔH fusion", `${s.Lf} kJ/kg`);
  row("ΔH vaporization", `${s.Lv} kJ/kg`);
  row("c (solid)", `${s.cs} kJ/kg·K`);
  row("c (liquid)", `${s.cl} kJ/kg·K`);
  row("c (gas)", `${s.cg} kJ/kg·K`);
  y += 8;

  section("Reflection prompts");
  const prompts = [
    "1. Why does temperature stay constant during melting and boiling, even though heat is added?",
    "2. Compare KE and PE at the start of the melt plateau vs. the end. What changed?",
    "3. Estimate the heat needed to take 1 kg from solid at the melting point to gas at the boiling point.",
    "4. Sketch the heating curve and label the five regions.",
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(40);
  prompts.forEach((p) => {
    const lines = doc.splitTextToSize(pdfSafe(p), PAGE_W - 2 * M);
    doc.text(lines, M, y);
    y += lines.length * 12 + 6;
    // 3 answer lines
    doc.setDrawColor(210);
    for (let i = 0; i < 3; i++) {
      doc.line(M, y, PAGE_W - M, y);
      y += 16;
    }
    y += 6;
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("elementpad.net · Generated on-device. Nothing is uploaded.", M, PAGE_H - 24);

  doc.save(`elementpad-phase-lab-${s.substanceName.toLowerCase()}.pdf`);
}